import { Injectable, Injector, runInInjectionContext } from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    ChildrenOutletContexts,
    OutletContext,
    PRIMARY_OUTLET,
    ResolveData,
    ResolveFn,
    Router,
} from '@angular/router';

import { getTokenWithInjector } from '@frontend/sports/common/core/utils/router';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { OnAppInit } from '@frontend/vanilla/core';
import { BehaviorSubject, EMPTY, Observable, debounceTime, from, isObservable, mergeMap, of, takeLast, tap } from 'rxjs';

import { FreshDataService } from './fresh-data.service';

@Injectable({ providedIn: 'root' })
export class RouteRefreshService implements OnAppInit {
    constructor(
        private refreshService: FreshDataService,
        private router: Router,
        private context: ChildrenOutletContexts,
        private injector: Injector,
        private productUtils: SportsProductUtils,
    ) {}

    onAppInit(): void {
        this.refreshService.reloadNeeded.pipe(debounceTime(500), this.productUtils.filterSportsEmitLast()).subscribe(() => this.refresh());
    }

    private async refresh(): Promise<void> {
        const initialContexts = this.context;

        const primaryOutlet = initialContexts.getContext(PRIMARY_OUTLET);
        const overlayOutlet = initialContexts.getContext('overlay');

        const outlets = [primaryOutlet, overlayOutlet];

        for (const outlet of outlets) {
            if (!outlet) {
                continue;
            }

            this.refreshForOutlet(outlet);
        }
    }

    private refreshForOutlet(initialOutlet: OutletContext): void {
        let outletChildren: ChildrenOutletContexts | undefined = initialOutlet?.children;
        let outletContext: OutletContext | undefined = initialOutlet;

        while (outletChildren) {
            const innerOutlet = outletChildren?.getContext(PRIMARY_OUTLET);

            if (innerOutlet) {
                outletContext = innerOutlet;
            }

            outletChildren = innerOutlet?.children;
        }

        if (outletContext?.route) {
            this.resolve(outletContext.route);
        }
    }

    private resolve(route: ActivatedRoute): void {
        if (!route.snapshot?.routeConfig?.resolve) {
            return;
        }

        this.load(route.snapshot.routeConfig.resolve, route.snapshot).subscribe((data) => {
            const subject = route.data as BehaviorSubject<unknown>;

            subject.next(data);
        });
    }

    private load(resolve: ResolveData, snapshot: ActivatedRouteSnapshot): Observable<unknown> {
        const data: Record<string, unknown> = {};
        const keys = Object.keys(resolve);

        return from(keys).pipe(
            mergeMap((key) => this.loadKey(key, resolve, snapshot).pipe(tap((result) => (data[key] = result)))),
            takeLast(1),
            mergeMap(() => {
                if (Object.keys(data).length === keys.length) {
                    return of(data);
                }

                return EMPTY;
            }),
        );
    }

    private loadKey(key: string, resolve: ResolveData, snapshot: ActivatedRouteSnapshot): Observable<unknown> {
        const { token, injector } = getTokenWithInjector<
            | {
                  resolve: ResolveFn<unknown>;
              }
            | ResolveFn<unknown>
        >(resolve[key], snapshot, this.injector);

        const result = runInInjectionContext(injector, () =>
            typeof token === 'function'
                ? token(snapshot, this.router.routerState.snapshot)
                : token.resolve(snapshot, this.router.routerState.snapshot),
        );

        if (isObservable(result)) {
            return result;
        }

        if (result instanceof Promise) {
            return from(result);
        }

        return of(result);
    }
}
